import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import isArray from 'lodash/isArray';
import { createSelector } from 'reselect';
import { toolsByType } from 'reducers/flowDesigner/toolStepDefinitions';
import { getBlockByIdMap, getInputs } from 'selectors/flowDesigner';
import { getConstantVariable } from 'utils/placeholderInput';
import { getCustomSteps } from 'selectors/flowDesigner/utils/getCustomSteps';

export const getCustomStepByIdMap = createSelector([getCustomSteps], steps =>
  Object.freeze(keyBy(steps, 'id')),
);

export const getCurrentCanvas = state => get(state, 'flowDesigner.planCanvas');

export const getSelectedConstant = state =>
  get(state, 'constantsModal.selectedConstant');

export const getConstants = state => get(state, 'constantsModal.constants');

function getIcon(step = {}) {
  const { icon, type } = step;
  if (icon) {
    return icon;
  }
  if (toolsByType[type]) {
    // Tools often have no id, or may have an id that doesn't point to a step or integration
    return toolsByType[type].icon;
  }
  return null;
}

export const getSelectedConstantUsage = createSelector(
  [
    getSelectedConstant,
    getInputs,
    getBlockByIdMap,
    getCurrentCanvas,
    getCustomStepByIdMap,
  ],
  (selected, inputs = [], blockMap, canvas, stepMap) => {
    // If no id, it is a new constant, and therefore cannot be used anywhere:
    if (!selected || selected.id == null) {
      return null;
    }

    if (!canvas || inputs.length === 0) {
      return selected.usage;
    }

    // filter out all usage by the current canvas and then recompute the usage
    const usagesByOthers = (selected.usage || []).filter(
      u => u.canvas.id !== canvas.id,
    );
    const constPattern = `\${${getConstantVariable(selected.name)}}`;

    const usedInputs = inputs.filter(
      i => i.value && isArray(i.value) && i.value.indexOf(constPattern) >= 0,
    );
    const usages = uniqBy(usedInputs, 'flowBlock.id')
      .map(i => blockMap[i.flowBlock.id])
      .filter(block => !!block)
      .map(block => {
        const step = stepMap[block.step?.id] || block.step || {};
        return {
          block: { ...block, step },
          canvas,
        };
      });

    // Add icons (run after the reducer to also add to `usagesByOthers`)
    const mixinIcons = usage => {
      if (!usage.block.step) {
        return usage;
      }

      return {
        ...usage,
        block: {
          ...usage.block,
          step: {
            ...usage.block.step,
            icon: getIcon(usage.block.step),
          },
        },
      };
    };
    return [...usagesByOthers, ...usages].map(mixinIcons);
  },
);

const getPlanFromCurrentPlan = state =>
  get(state, 'currentCommunicationPlan.planId') ||
  get(state, 'currentCommunicationPlan.workflowHeaderPlanId');

const getPlanFromIntegrationConfig = state =>
  get(state, 'integrationConfigs.oneConfig.data.id');

export const getPlanId = createSelector(
  [getPlanFromCurrentPlan, getPlanFromIntegrationConfig],
  (idFromCurrentPlan, idFromIntegrationConfig) =>
    idFromCurrentPlan || idFromIntegrationConfig,
);

export const getConstantsModalIsOpen = state => !!state.constantsModal.show;
